import { useLingui } from '@lingui/react/macro'
import { Box } from 'designSystem/components/Box'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
} from 'designSystem/components/DropdownMenu'
import { RevisionDropdownTrigger } from 'features/knowledgeDocumentViewer/components/metadata/RevisionDropdownTrigger'
import { RevisionFamilyDropdownItem } from 'features/knowledgeDocumentViewer/components/metadata/RevisionFamilyDropdownItem'
import type { RevisionMenuItemView } from 'features/knowledgeDocumentViewer/utils/revisionHelpers'
import React, { useState } from 'react'

export type RevisionFamilyDropdownProps<TRevision> = {
  revisions: readonly TRevision[]
  toMenuItem: (revision: TRevision) => RevisionMenuItemView
  isOutdated: boolean
  isLoading?: boolean
  triggerLabel: string
  outdatedTooltip: string
  size: 'xs' | 'lg'
  onSelectRevision: (revision: TRevision) => void
}

export function RevisionFamilyDropdown<TRevision>({
  revisions,
  toMenuItem,
  isOutdated,
  isLoading = false,
  triggerLabel,
  outdatedTooltip,
  size,
  onSelectRevision,
}: RevisionFamilyDropdownProps<TRevision>): React.ReactElement {
  const { t } = useLingui()
  const [isOpen, setIsOpen] = useState(false)
  const revisionCount = revisions.length

  return (
    <Box shrink={false}>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <RevisionDropdownTrigger
          label={triggerLabel}
          isOutdated={isOutdated}
          isOpen={isOpen}
          outdatedTooltip={outdatedTooltip}
          size={size}
        />
        <DropdownMenuContent
          align='end'
          sideOffset={4}
          width='wide'
          overflow='hidden'>
          <DropdownMenuLabel variant='emphasis'>
            {t`Revisions (${revisionCount})`}
          </DropdownMenuLabel>
          {revisions.map((revision) => {
            const item = toMenuItem(revision)
            return (
              <RevisionFamilyDropdownItem
                key={item.key}
                item={item}
                isLoading={isLoading}
                onSelect={() => onSelectRevision(revision)}
              />
            )
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </Box>
  )
}
